/**
 * useSwipeToDismiss - Custom hook for swipe-to-dismiss functionality
 * 
 * Allows users to swipe right to dismiss drawers/modals
 * Perfect for field workers with dirty hands or in a hurry
 */

import { useState, useRef, useCallback } from 'react';

export const useSwipeToDismiss = (onDismiss, options = {}) => {
  const {
    threshold = 100,        // Distance in px to trigger dismiss
    direction = 'right',    // 'right' or 'down'
    enabled = true,         // Allow disabling
    onSwipeStart = null,    // Callback when swipe starts
    onSwipeMove = null,     // Callback during swipe (receives progress 0-1)
    onSwipeCancel = null,   // Callback when swipe doesn't meet threshold
  } = options;

  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startPos = useRef({ x: 0, y: 0 });
  const isHorizontalSwipe = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (!enabled) return;
    
    startPos.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
    isHorizontalSwipe.current = null;
    setIsDragging(true);
    
    if (onSwipeStart) onSwipeStart();
  }, [enabled, onSwipeStart]);

  const handleTouchMove = useCallback((e) => {
    if (!enabled || !isDragging) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = currentX - startPos.current.x;
    const deltaY = currentY - startPos.current.y;

    // Determine swipe direction on first significant movement
    if (isHorizontalSwipe.current === null && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
      isHorizontalSwipe.current = Math.abs(deltaX) > Math.abs(deltaY);
    }

    // Only handle horizontal swipes for 'right' direction
    if (direction === 'right' && isHorizontalSwipe.current) {
      // Only allow swiping right (positive deltaX)
      const offset = Math.max(0, deltaX);
      setSwipeOffset(offset);
      
      if (onSwipeMove) {
        const progress = Math.min(1, offset / threshold);
        onSwipeMove(progress);
      }
    }
    
    // Handle vertical swipes for 'down' direction
    if (direction === 'down' && !isHorizontalSwipe.current) {
      const offset = Math.max(0, deltaY);
      setSwipeOffset(offset);
      
      if (onSwipeMove) {
        const progress = Math.min(1, offset / threshold);
        onSwipeMove(progress);
      }
    }
  }, [enabled, isDragging, direction, threshold, onSwipeMove]);

  const handleTouchEnd = useCallback(() => {
    if (!enabled) return;
    
    setIsDragging(false);
    
    if (swipeOffset >= threshold) {
      // Threshold met - dismiss
      onDismiss();
    } else if (swipeOffset > 0) {
      // Threshold not met - cancel and reset
      if (onSwipeCancel) onSwipeCancel();
    }
    
    setSwipeOffset(0);
    isHorizontalSwipe.current = null;
  }, [enabled, swipeOffset, threshold, onDismiss, onSwipeCancel]);

  // Calculate transform style based on swipe direction
  const getSwipeStyle = useCallback(() => {
    if (swipeOffset === 0) return {};
    
    if (direction === 'right') {
      return {
        transform: `translateX(${swipeOffset}px)`,
        transition: isDragging ? 'none' : 'transform 0.2s ease-out'
      };
    }
    
    if (direction === 'down') {
      return {
        transform: `translateY(${swipeOffset}px)`,
        transition: isDragging ? 'none' : 'transform 0.2s ease-out'
      };
    }
    
    return {};
  }, [swipeOffset, direction, isDragging]);

  // Progress from 0 to 1 based on threshold
  const swipeProgress = Math.min(1, swipeOffset / threshold);

  return {
    // Touch handlers to spread onto the element
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    // Style to apply for the swipe animation
    style: getSwipeStyle(),
    // Current state
    swipeOffset,
    swipeProgress,
    isDragging,
  };
};

export default useSwipeToDismiss;
